import { isFieldErrorResponse, useApiTradeV1TradeCompaniesRetrieve } from 'api';
import { convertToMaterialComponentInput, useNotification } from 'features';
import { Form, useForm, yupResolver } from 'form';
import { useTranslation } from 'i18n';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Page } from 'ui';

import { BuyerMaterialSpecificationFormFields } from '../../components/BuyerMaterialSpecificationFormFields/BuyerMaterialSpecificationFormFields';
import { useCreateBuyerMaterialSpecification } from '../../hooks/useCreateBuyerMaterialSpecification/useCreateBuyerMaterialSpecification';
import { type BuyerMaterialSpecificationFormType, BuyerMaterialSpecificationSchema } from '../schema';

export const BuyerMaterialSpecificationsAddPage = () => {
  const { t } = useTranslation('orders');
  const { open } = useNotification();
  const navigate = useNavigate();
  const form = useForm<BuyerMaterialSpecificationFormType, unknown, BuyerMaterialSpecificationFormType>({
    defaultValues: {
      packaging_specification_ids: [],
    },
    resolver: yupResolver(BuyerMaterialSpecificationSchema),
  });
  const { companyId } = useParams();
  const { data: tradeCompany } = useApiTradeV1TradeCompaniesRetrieve(Number(companyId), {});

  useEffect(() => {
    if (tradeCompany) {
      form.setValue('buyer_id', tradeCompany.id);
    }
  }, [form, tradeCompany]);

  const onCreateError = (error: Error) => {
    if (isFieldErrorResponse(error)) {
      for (const field of Object.keys(error.response?.data)) {
        form.setError(field as keyof BuyerMaterialSpecificationFormType, {
          message: error.response.data[field],
        });
      }
    } else {
      open({
        message: t('buyerMaterialSpecifications.createPage.notifications.createFailed', {
          values: { error: String(error) },
        }),
        type: 'error',
      });
    }
  };

  const { createBuyerMaterialSpecification, isPending } = useCreateBuyerMaterialSpecification({
    onError: onCreateError,
  });

  const onSubmit = async (values: BuyerMaterialSpecificationFormType) => {
    const materialComponents = convertToMaterialComponentInput(values.material_qualities);
    await createBuyerMaterialSpecification({
      buyerId: values.buyer_id,
      document: values.document || undefined,
      materialColourId: values.material_colour_id,
      materialComponents,
      materialFormId: values.material_form_id,
      materialGradeId: values.material_grade_id,
      materialId: values.material_id,
      notes: values.notes,
      packagingSpecificationIds: values.packaging_specification_ids,
    });
  };
  return (
    <Page>
      <Page.Title title={t('buyerMaterialSpecifications.createPage.title')} />
      <Page.Divider />
      <Form {...form} onSubmit={onSubmit}>
        <BuyerMaterialSpecificationFormFields />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button onClick={() => navigate(-1)} variant="secondary">
            {t('buyerMaterialSpecifications.createPage.actions.cancel')}
          </Button>
          <Button loading={isPending} type="submit">
            {t('buyerMaterialSpecifications.createPage.actions.save')}
          </Button>
        </Box>
      </Form>
    </Page>
  );
};
